/**
 * Lazy Firebase Auth singleton. Kept in its own module so we only pay the
 * ~35KB bundle cost on pages that actually call `signInWithPopup`.
 *
 * Plan 029: Firebase Auth is used ONLY for federated sign-in (Google today).
 * The Firebase session itself is disposable — we exchange the ID token for
 * Backstop's own JWT immediately, then let Firebase's session drop. Backstop's
 * refresh-token flow remains the session-of-record.
 *
 * Env inputs come from `.env.production` (see `pwa/.env.production`):
 *   VITE_FIREBASE_API_KEY   — Firebase Web App public API key.
 *   VITE_FIREBASE_PROJECT_ID — matches CP's BS_FIREBASE_PROJECT_ID.
 * Both are safe to ship in the client bundle; access is restricted server-side
 * by the authorised-domains list in the Firebase console.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  type Auth,
  type UserCredential,
} from 'firebase/auth';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

function firebaseConfig(): {
  apiKey: string;
  authDomain: string;
  projectId: string;
} {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as
    string | undefined;
  if (
    apiKey === undefined ||
    apiKey === '' ||
    projectId === undefined ||
    projectId === ''
  ) {
    throw new Error(
      'firebase auth env not set (VITE_FIREBASE_API_KEY, VITE_FIREBASE_PROJECT_ID)',
    );
  }
  return {
    apiKey,
    authDomain: `${projectId}.firebaseapp.com`,
    projectId,
  };
}

/**
 * True iff Firebase env vars are present in the build. Used by the sign-in
 * pages to decide whether to render the "Continue with Google" button.
 * Development builds without a `.env.local` fall back to email + password.
 */
export function firebaseConfigured(): boolean {
  try {
    firebaseConfig();
    return true;
  } catch {
    return false;
  }
}

function auth(): Auth {
  if (_auth !== null) return _auth;
  _app ??= initializeApp(firebaseConfig());
  _auth = getAuth(_app);
  return _auth;
}

const googleProvider = new GoogleAuthProvider();

/**
 * Open a Google account-picker popup. Returns the ID token that the CP
 * verifier will accept. Discards the Firebase session; only the JWT flows
 * onward.
 */
export async function signInWithGoogle(): Promise<string> {
  const cred: UserCredential = await signInWithPopup(auth(), googleProvider);
  const token = await cred.user.getIdToken(/* forceRefresh */ false);
  return token;
}

/**
 * Firebase Auth password signup (plan 032). Creates the Firebase user +
 * returns their fresh ID token for the CP callback. Throws Firebase's
 * own `auth/*` errors on failure (email-already-in-use, weak-password,
 * etc.); [`friendlyFirebaseError`] maps them to plain-language messages.
 */
export async function signUpWithPassword(
  email: string,
  password: string,
): Promise<string> {
  const cred = await createUserWithEmailAndPassword(auth(), email, password);
  return cred.user.getIdToken(false);
}

/** Firebase Auth password sign-in (plan 032). */
export async function signInWithPassword(
  email: string,
  password: string,
): Promise<string> {
  const cred = await signInWithEmailAndPassword(auth(), email, password);
  return cred.user.getIdToken(false);
}

/**
 * Trigger a Firebase-hosted password-reset email. Firebase composes and
 * sends the email; the user clicks a link and picks a new password on a
 * Firebase-hosted page. Nothing else to do on our side.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth(), email);
}

/**
 * Map Firebase Auth error codes to friendly user-facing sentences.
 * Falls back to `.message` on unrecognised errors.
 */
export function friendlyFirebaseError(e: unknown): string {
  const msg = (e as Error).message ?? String(e);
  if (msg.includes('auth/email-already-in-use')) {
    return 'An account with that email already exists. Try signing in instead.';
  }
  if (msg.includes('auth/invalid-email')) {
    return "That doesn't look like a valid email address.";
  }
  if (msg.includes('auth/weak-password')) {
    return 'Password is too weak — Firebase requires at least 6 characters.';
  }
  if (
    msg.includes('auth/wrong-password') ||
    msg.includes('auth/user-not-found')
  ) {
    return 'Wrong email or password.';
  }
  if (msg.includes('auth/invalid-credential')) {
    return 'Wrong email or password.';
  }
  if (msg.includes('auth/too-many-requests')) {
    return 'Too many attempts. Try again in a few minutes, or reset your password.';
  }
  if (msg.includes('auth/network-request-failed')) {
    return 'Network error talking to Firebase. Check your connection.';
  }
  if (msg.includes('popup-closed-by-user') || msg.includes('cancelled')) {
    return '';
  }
  return msg;
}
